import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    HiOutlineCurrencyDollar,
    HiOutlineArrowRight,
} from "react-icons/hi";
import Card from "../shared/Card";
import Badge from "../shared/Badge";
import { getRevenueByMember } from "../../redux/api/analyticsAPI";
import { dateRangeToBounds, formatRevenue } from "../../utils/revenueAnalytics";

const TOP_LIMIT = 10;

const RevenueByMemberPanel = ({ organisationId, dateRange }) => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const { startDate, endDate } = useMemo(
        () => dateRangeToBounds(dateRange),
        [dateRange]
    );

    useEffect(() => {
        if (!organisationId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        getRevenueByMember({
            organisationId,
            startDate,
            endDate,
            page: 1,
            pageSize: TOP_LIMIT,
        }).then((res) => {
            if (cancelled) return;
            if (res.error) {
                setError(res.error);
                setRows([]);
                setTotal(0);
            } else {
                setRows(res.data?.rows || []);
                setTotal(res.data?.total || 0);
            }
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [organisationId, startDate, endDate]);

    const hasMore = total > TOP_LIMIT;

    return (
        <Card variant="default" className="!p-0 overflow-hidden">
            <div className="p-5 border-b border-hairline flex items-center justify-between">
                <h2 className="text-title-md text-ink flex items-center gap-2">
                    <HiOutlineCurrencyDollar className="h-5 w-5 text-ink" />
                    <span>Revenue by Member</span>
                </h2>
                <Badge variant="default">Top {Math.min(TOP_LIMIT, total)}</Badge>
            </div>

            <div className="px-5 py-3 bg-surface-soft border-b border-hairline">
                <div className="flex text-caption text-muted uppercase tracking-wide">
                    <div className="w-2/5 px-2">Member</div>
                    <div className="w-1/5 px-2 text-center">Submissions</div>
                    <div className="w-1/5 px-2 text-right">Points</div>
                    <div className="w-1/5 px-2 text-right">Revenue</div>
                </div>
            </div>

            {loading ? (
                <div className="px-5 py-8 text-center text-body-md text-muted">Loading…</div>
            ) : error ? (
                <div className="px-5 py-8 text-center text-body-md text-signature-coral">{error}</div>
            ) : rows.length === 0 ? (
                <div className="px-5 py-8 text-center text-body-md text-muted">
                    No members with submissions in this range.
                </div>
            ) : (
                <ul>
                    {rows.map((row) => (
                        <li
                            key={row.identification}
                            className="border-b border-hairline px-5 py-4"
                        >
                            <div className="flex items-center">
                                <div className="w-2/5 px-2">
                                    <div className="text-body-md font-medium text-ink">
                                        {row.name || "No Name"}
                                    </div>
                                    <div
                                        className="text-body-md mt-1 truncate max-w-[260px] text-muted"
                                        title={row.identification}
                                    >
                                        ID: {row.identification}
                                    </div>
                                </div>
                                <div className="w-1/5 px-2 text-center text-body-md text-body">
                                    {row.submissionsCount.toLocaleString()}
                                </div>
                                <div className="w-1/5 px-2 text-right text-body-md text-body">
                                    {row.totalPoints.toLocaleString()}
                                </div>
                                <div className="w-1/5 px-2 text-right text-body-md font-medium text-ink">
                                    {formatRevenue(row.totalRevenue)}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {hasMore && (
                <div className="px-5 py-4 flex items-center justify-between bg-surface-soft">
                    <span className="text-body-md text-muted">
                        Showing top {TOP_LIMIT} of {total.toLocaleString()} members
                    </span>
                    <Link
                        to="/analytics/revenue-by-member"
                        className="inline-flex items-center gap-1 text-body-md text-link hover:text-link-active"
                    >
                        View all
                        <HiOutlineArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            )}
        </Card>
    );
};

export default RevenueByMemberPanel;
